import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  FaShieldAlt,
  FaLock,
  FaTh,
  FaDatabase,
  FaEye,
  FaExclamationTriangle,
  FaCogs,
} from 'react-icons/fa';

const navy = '#031a38';
const navyMid = '#042248';
const chalk = 'rgba(255, 255, 255, 0.88)';
const lineSoft = 'rgba(255, 255, 255, 0.42)';

const HeroSection = styled.section`
  position: relative;
  padding: 6.5rem 0 4.5rem;
  background: radial-gradient(120% 90% at 50% -15%, ${navyMid} 0%, ${navy} 52%, #020f22 100%);
  overflow: hidden;
  color: ${chalk};

  @media (max-width: 768px) {
    padding: 5.75rem 0 3.5rem;
  }
`;

const PaperGrain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.07;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  mix-blend-mode: overlay;
`;

const GraphPaper = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.14;
  background-image:
    linear-gradient(${lineSoft} 1px, transparent 1px),
    linear-gradient(90deg, ${lineSoft} 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: linear-gradient(90deg, transparent 0%, black 12%, black 88%, transparent 100%);
  filter: blur(0.35px);
`;

const Stage = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1120px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 clamp(16px, 4vw, 48px);
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 44rem;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 2.75rem;
`;

const Kicker = styled(motion.span)`
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.52);
  margin-bottom: 1rem;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(1.65rem, 3.6vw, 2.85rem);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #ffffff;
  margin-bottom: 1.1rem;
  text-shadow:
    0 0 42px rgba(180, 210, 255, 0.12),
    0 2px 0 rgba(0, 0, 0, 0.35);
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(0.95rem, 1.35vw, 1.08rem);
  font-weight: 500;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.72);
  max-width: 38rem;
  margin-left: auto;
  margin-right: auto;
`;

const Lead = styled(motion.p)`
  margin-top: 1.35rem;
  font-size: 0.92rem;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.58);
  max-width: 36rem;
  margin-left: auto;
  margin-right: auto;
`;

const SketchCell = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1.15rem 1.2rem 1.25rem;
  min-height: 132px;
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.045) 0%, rgba(255, 255, 255, 0) 52%);
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    border: 1px solid rgba(255, 255, 255, 0.38);
    border-radius: 1px;
    filter: blur(0.55px);
    opacity: 0.75;
    z-index: -1;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 1px solid rgba(255, 255, 255, 0.52);
    border-radius: 1px;
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.06),
      0 0 18px rgba(140, 190, 255, 0.06);
    pointer-events: none;
    z-index: -1;
  }
`;

const LineOverhang = styled.span`
  position: absolute;
  z-index: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.55) 18%,
    rgba(255, 255, 255, 0.55) 82%,
    transparent 100%
  );
  filter: blur(0.45px);
  pointer-events: none;
  opacity: 0.9;

  &.h {
    height: 1px;
    left: -10px;
    right: -10px;
  }

  &.v {
    width: 1px;
    top: -10px;
    bottom: -10px;
    background: linear-gradient(
      180deg,
      transparent 0%,
      rgba(255, 255, 255, 0.55) 18%,
      rgba(255, 255, 255, 0.55) 82%,
      transparent 100%
    );
  }

  &.top {
    top: 0;
  }
  &.bottom {
    bottom: 0;
  }
  &.left {
    left: 0;
  }
  &.right {
    right: 0;
  }
`;

const BlueprintGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr minmax(132px, 0.82fr);
  grid-template-rows: minmax(150px, 1fr) minmax(150px, 1fr) minmax(108px, auto);
  gap: 16px;
  width: 100%;
  max-width: 860px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 2.75rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: none;
    max-width: none;
  }
`;

const CellSecurity = styled(SketchCell)`
  grid-column: 1 / 3;
  grid-row: 1 / 3;
  min-height: 300px;
  padding: 1.5rem 1.35rem 1.5rem;

  @media (max-width: 768px) {
    grid-column: 1;
    grid-row: auto;
    min-height: 220px;
  }
`;

const CellEncrypt = styled(SketchCell)`
  grid-column: 3;
  grid-row: 1;

  @media (max-width: 768px) {
    grid-column: 1;
    grid-row: auto;
  }
`;

const CellFirewall = styled(SketchCell)`
  grid-column: 3;
  grid-row: 2;

  @media (max-width: 768px) {
    grid-column: 1;
    grid-row: auto;
  }
`;

const CellNet = styled(SketchCell)`
  grid-column: 1;
  grid-row: 3;
  min-height: 104px;

  @media (max-width: 768px) {
    grid-column: 1;
    grid-row: auto;
  }
`;

const CellData = styled(SketchCell)`
  grid-column: 2;
  grid-row: 3;
  min-height: 104px;

  @media (max-width: 768px) {
    grid-column: 1;
    grid-row: auto;
  }
`;

const CellIncident = styled(SketchCell)`
  grid-column: 3;
  grid-row: 3;
  min-height: 104px;

  @media (max-width: 768px) {
    grid-column: 1;
    grid-row: auto;
  }
`;

const CellLabel = styled.span`
  position: relative;
  z-index: 2;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #ffffff;
  text-shadow: 0 1px 0 rgba(0, 0, 0, 0.45);
  text-align: center;
  align-self: center;
  width: 100%;
`;

const CellLabelLg = styled(CellLabel)`
  font-size: clamp(1.35rem, 2.4vw, 1.85rem);
`;

const CellLabelMd = styled(CellLabel)`
  font-size: clamp(0.78rem, 1.15vw, 0.95rem);
`;

const CellLabelSm = styled(CellLabel)`
  font-size: clamp(0.68rem, 1vw, 0.82rem);
`;

const CellIcon = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(2rem, 3.5vw, 3rem);
  color: rgba(255, 255, 255, 0.92);
  filter: drop-shadow(0 0 10px rgba(200, 220, 255, 0.12));

  ${CellSecurity} & {
    font-size: clamp(3rem, 6vw, 4.5rem);
    flex: 1;
    align-items: center;
  }

  ${CellNet}, ${CellData}, ${CellIncident} & {
    font-size: 1.55rem;
    margin-top: 0.35rem;
  }
`;

const Actions = styled(motion.div)`
  display: flex;
  gap: 0.85rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 2.5rem;
`;

const PrimaryBtn = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.35rem;
  background: rgba(255, 255, 255, 0.1);
  color: #fff !important;
  text-decoration: none;
  border-radius: 2px;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  border: 1px solid rgba(255, 255, 255, 0.45);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.06), 0 0 20px rgba(120, 180, 255, 0.08);
  transition: background 0.2s ease, transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.16);
    border-color: rgba(255, 255, 255, 0.65);
    transform: translateY(-2px);
  }
`;

const GhostBtn = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.35rem;
  color: rgba(255, 255, 255, 0.92) !important;
  text-decoration: none;
  border-radius: 2px;
  font-weight: 700;
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  border: 1px dashed rgba(255, 255, 255, 0.4);
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.75);
    background: rgba(255, 255, 255, 0.06);
    transform: translateY(-2px);
  }
`;

const StatsRow = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  max-width: 860px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  position: relative;
  padding: 1rem 1rem 1.05rem;
  text-align: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 1px;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.04);
`;

const StatNumber = styled.div`
  font-size: 1.55rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: #ffffff;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.48);
`;

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
});

const CellLines = () => (
  <>
    <LineOverhang className="h top" aria-hidden />
    <LineOverhang className="h bottom" aria-hidden />
    <LineOverhang className="v left" aria-hidden />
    <LineOverhang className="v right" aria-hidden />
  </>
);

const Hero = () => {
  return (
    <HeroSection id="home">
      <PaperGrain aria-hidden />
      <GraphPaper aria-hidden />

      <Stage>
        <HeroContent>
          <Kicker {...fadeUp(0)}>Информационная безопасность</Kicker>
          <HeroTitle {...fadeUp(0.04)}>
            Корпоративная защита и&nbsp;надёжная автоматизация
          </HeroTitle>
          <HeroSubtitle {...fadeUp(0.1)}>
            Приоритет — безопасное и бесперебойное функционирование промышленных объектов и ИТ-контуров.
          </HeroSubtitle>
          <Lead {...fadeUp(0.16)}>
            С 2005 года ООО «Энткор-е» разрабатывает и поставляет системы промышленной автоматизации с особым вниманием к информационной безопасности.
          </Lead>
        </HeroContent>

        <BlueprintGrid {...fadeUp(0.12)}>
          <CellSecurity>
            <CellLines />
            <CellLabelLg>Безопасность</CellLabelLg>
            <CellIcon>
              <FaShieldAlt aria-hidden />
            </CellIcon>
          </CellSecurity>

          <CellEncrypt>
            <CellLines />
            <CellLabelMd>Шифрование</CellLabelMd>
            <CellIcon>
              <FaLock aria-hidden />
            </CellIcon>
          </CellEncrypt>

          <CellFirewall>
            <CellLines />
            <CellLabelMd>Межсетевой экран</CellLabelMd>
            <CellIcon>
              <FaTh aria-hidden />
            </CellIcon>
          </CellFirewall>

          <CellNet>
            <CellLines />
            <CellLabelSm>Мониторинг сетей</CellLabelSm>
            <CellIcon>
              <FaEye aria-hidden />
            </CellIcon>
          </CellNet>

          <CellData>
            <CellLines />
            <CellLabelSm>Защита данных</CellLabelSm>
            <CellIcon>
              <FaDatabase aria-hidden />
            </CellIcon>
          </CellData>

          <CellIncident>
            <CellLines />
            <CellLabelSm>Реагирование на инциденты</CellLabelSm>
            <CellIcon>
              <FaExclamationTriangle aria-hidden />
            </CellIcon>
          </CellIncident>
        </BlueprintGrid>

        <Actions {...fadeUp(0.22)}>
          <PrimaryBtn href="#products">
            <FaCogs aria-hidden /> Продукты
          </PrimaryBtn>
          <GhostBtn href="#contact">
            <FaShieldAlt aria-hidden /> Связь
          </GhostBtn>
        </Actions>

        <StatsRow {...fadeUp(0.28)}>
          <StatCard>
            <StatNumber>2005</StatNumber>
            <StatLabel>Основание</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>18+</StatNumber>
            <StatLabel>Лет опыта</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>100%</StatNumber>
            <StatLabel>Надёжность</StatLabel>
          </StatCard>
        </StatsRow>
      </Stage>
    </HeroSection>
  );
};

export default Hero;
